import { Box, Flex, useOutsideClick } from '@televet/kibble-ui/build/chakra';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { taskGroupsMap } from 'pages/Tasks/constants';
import { TaskGroup } from 'pages/Tasks/types';
import React, { useMemo, useRef } from 'react';
import { PopoverPlacement } from 'shared/components/Dropdown';
import { formatFilterOptionList } from 'shared/components/Filter/utils';
import popperMaxSizeModifiers from 'shared/utils/popperMaxSizeModifiers';
import { Filter } from '../../../../shared/components/Filter';
import { useTasksContext } from '../../context/TasksContext';
import { Popover } from '@televet/kibble-ui/build/components/Popover';

type TaskFilterProps = {
  isOpen: boolean;
  trigger: React.ReactNode;
  onClose: () => void;
  activeGroup: TaskGroup;
};

export const TaskFilter = ({ isOpen, trigger, onClose, activeGroup }: TaskFilterProps): JSX.Element => {
  const popoverContainerRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick({
    ref: popoverContainerRef,
    handler: () => {
      if (isOpen) {
        onClose();
      }
    },
  });

  const { filters, setFilters, clearFilters, quickSelectOptions, teamMembersOptions, statusOptions } =
    useTasksContext();
  const { assigneesOptions, statuses } = useMemo(() => {
    const assigneesOptions = [
      formatFilterOptionList(quickSelectOptions, 'name', 'Quick Select'),
      formatFilterOptionList(teamMembersOptions, 'name', 'Team Members'),
    ];
    const statuses = formatFilterOptionList(statusOptions, 'name');
    return { assigneesOptions, statuses };
  }, [quickSelectOptions, statusOptions, teamMembersOptions]);

  return (
    <>
      <Popover
        anchor={trigger}
        data-testid="tasks-filter-popover"
        isOpen={isOpen}
        closeOnBlur={false}
        isLazy={true}
        placement={PopoverPlacement.BottomStart}
        strategy="fixed"
        preventOverflow={true}
        boundary={document.body}
        eventListeners={{ resize: true }}
        modifiers={popperMaxSizeModifiers}
        onClose={onClose}
        size="md"
        variant="default"
      >
        <Box ref={popoverContainerRef} m={-3}>
          <Flex
            align="center"
            justify="space-between"
            p={3}
            pl={4}
            borderBottom="1px"
            borderBottomColor="border.default"
          >
            <Heading size="md" fontWeight="bold">
              Filters
            </Heading>
            <Button
              data-testid="tasks-filter-clear-filter-button"
              variant="ghostNeutral"
              size="sm"
              iconName="arrowCircle"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Flex>
          <Box borderBottom="1px" borderBottomColor="border.default">
            {activeGroup === taskGroupsMap.needsAttention && (
              <Filter
                buttonTestId="tasks-filter-add-status-button"
                label="Status"
                selectedOptions={filters[activeGroup.name].status}
                showSearchBar={false}
                options={statuses}
                onValueChange={(options): void => {
                  setFilters((currentFilters) => ({
                    ...currentFilters,
                    [activeGroup.name]: {
                      ...currentFilters[activeGroup.name],
                      status: options,
                    },
                  }));
                }}
                filterContainerWidth="170px"
                renderOption={({ ...option }): JSX.Element => (
                  <Tag
                    key={option.label}
                    color="white"
                    bg={option.color}
                    size="sm"
                    alignSelf="center"
                    label={option.label}
                    data-testid={`tasks-filter-status-tag-${option.label}`}
                  />
                )}
              />
            )}
            <Filter
              label={activeGroup === taskGroupsMap.needsAttention ? 'Assignees' : 'Completed By'}
              selectedOptions={filters[activeGroup.name].assignees}
              optionGroups={assigneesOptions}
              onValueChange={(options): void => {
                setFilters((currentFilters) => ({
                  ...currentFilters,
                  [activeGroup.name]: {
                    ...currentFilters[activeGroup.name],
                    assignees: options,
                  },
                }));
              }}
              showSearchBar={true}
              filterContainerWidth="320px"
              isLastRow={true}
              isGrouped={true}
              buttonTestId={`tasks-filter-add-${
                activeGroup === taskGroupsMap.needsAttention ? 'assignee' : 'completed-by'
              }-button`}
            />
          </Box>
          <Flex w="100%" justify="flex-end" p={3}>
            <Button size="sm" onClick={onClose} data-testid="tasks-filter-done-button">
              Done
            </Button>
          </Flex>
        </Box>
      </Popover>
    </>
  );
};
